import {
  BookMarked,
  ChartLine,
  CircleDollarSign,
  FlaskConical,
  Flag,
  House,
  LayoutDashboard,
  PlayCircle,
  Radar,
  Settings,
  Sparkles,
  Users,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

export type SectionKey =
  | "home"
  | "ask"
  | "insights"
  | "dashboards"
  | "replays"
  | "people"
  | "cohorts"
  | "revenue"
  | "flags"
  | "experiments"
  | "catalog"
  | "findings"
  | "settings";

export interface Section {
  key: SectionKey;
  label: string;
  icon: LucideIcon;
  /** AI surfaces wear the reserved violet. */
  ai?: boolean;
  /** Built in this release? Unbuilt sections render a "coming later" page, not a broken one. */
  live: boolean;
  summary: string;
  planned: string[];
  release: string;
}

export const SECTIONS: Section[] = [
  {
    key: "home",
    label: "Home",
    icon: House,
    live: true,
    summary: "Your project at a glance.",
    planned: [],
    release: "",
  },
  {
    key: "ask",
    label: "Ask",
    icon: Sparkles,
    ai: true,
    live: true,
    summary: "Ask questions about your product in plain language and get answers you can verify.",
    planned: [
      "Every number in an answer is linked to the query that produced it",
      "Confidence computed from sample size, freshness and sampling — never guessed",
      "Runs inside your project's region, like the rest of your data",
    ],
    release: "Wave 3",
  },
  {
    key: "insights",
    label: "Insights",
    icon: ChartLine,
    live: true,
    summary: "Trends, funnels, retention and paths.",
    planned: [],
    release: "",
  },
  {
    key: "dashboards",
    label: "Dashboards",
    icon: LayoutDashboard,
    live: true,
    summary: "Pin insights side by side and share them with your team.",
    planned: ["Save any insight to a dashboard", "Shared dashboard filters and date range", "A pinned dashboard on Home"],
    release: "Wave 2",
  },
  {
    key: "replays",
    label: "Replays",
    icon: PlayCircle,
    live: true,
    summary: "Watch real sessions on web and mobile, masked by default.",
    planned: [
      "Web replay and native mobile wireframe replay",
      "Crash, ANR and feature-flag chips on the timeline",
      "Jump straight from a funnel drop-off to the sessions behind it",
    ],
    release: "Wave 3",
  },
  {
    key: "people",
    label: "People",
    icon: Users,
    live: true,
    summary: "Every person and group, with an identity graph you can audit and undo.",
    planned: ["Person profiles and event timelines", "Merge history with one-click unmerge", "Groups for B2B accounts"],
    release: "Wave 2",
  },
  {
    key: "cohorts",
    label: "Cohorts",
    icon: UsersRound,
    live: true,
    summary: "Define groups of people by what they did and who they are.",
    planned: ["Behavioural and property-based cohorts", "Use cohorts as filters and breakdowns", "Static cohorts from CSV"],
    release: "Wave 2",
  },
  {
    key: "revenue",
    label: "Revenue",
    icon: CircleDollarSign,
    live: false,
    summary: "Revenue from Stripe, RevenueCat, App Store and Google Play, joined to product usage.",
    planned: ["Connect billing providers in minutes", "Revenue as a funnel step and experiment metric", "MRR, churn and LTV by cohort"],
    release: "Wave 4",
  },
  {
    key: "flags",
    label: "Flags",
    icon: Flag,
    live: true,
    summary: "Feature flags with local evaluation and per-environment rollout.",
    planned: ["Percentage and property-based rollouts", "Separate configuration per environment", "Every change recorded as an annotation"],
    release: "Wave 3",
  },
  {
    key: "experiments",
    label: "Experiments",
    icon: FlaskConical,
    live: true,
    summary: "A/B tests with sequential statistics that are safe to peek at.",
    planned: ["Sequential, frequentist and Bayesian analysis", "CUPED variance reduction", "Sample-ratio-mismatch checks that block bad verdicts"],
    release: "Wave 3",
  },
  {
    key: "catalog",
    label: "Catalog",
    icon: BookMarked,
    live: true,
    summary: "Your tracking plan: every event, the question it answers, and whether it's firing.",
    planned: ["Planned → implemented → verified event lifecycle", "Live event stream", "Typed SDK wrappers generated from the plan"],
    release: "Wave 2",
  },
  {
    key: "findings",
    label: "Findings",
    icon: Radar,
    live: false,
    summary: "Anomalies in your key metrics, detected and explained before you go looking.",
    planned: ["Seasonality-aware anomaly detection", "Root cause across platform, version, country and flags", "Delivered to Slack, email or WhatsApp"],
    release: "Wave 4",
  },
  {
    key: "settings",
    label: "Settings",
    icon: Settings,
    live: true,
    summary: "Project, keys and personal tokens.",
    planned: [],
    release: "",
  },
];

export const sectionByKey = (key: SectionKey): Section => {
  const s = SECTIONS.find((x) => x.key === key);
  if (!s) throw new Error(`unknown section ${key}`);
  return s;
};

export const INSIGHT_TABS = [
  { key: "saved", label: "Saved", live: true },
  { key: "trends", label: "Trends", live: true },
  { key: "funnels", label: "Funnels", live: true },
  { key: "retention", label: "Retention", live: true },
  { key: "paths", label: "Paths", live: true },
  { key: "stickiness", label: "Stickiness", live: false },
  { key: "lifecycle", label: "Lifecycle", live: false },
  { key: "sql", label: "SQL", live: true },
] as const;
